import { GPXOption, ParsedGPX, RouteGPX, TrackGPX } from "@iventis/types";
import { FeatureCollection, LineString } from "geojson";
import { compareAsc } from "date-fns";
import { parseGPX } from "./parseGPX";
import { fileToString } from "../converters/json";
import { isValidDate } from "../general";

/**
 * Converts a GPX file to a GeoJSON Feature collection.
 * @returns An object containing:
 *  the options available inside the gpx content (i.e. routes and tracks),
 *  the selected option (based on whether the provided option exists or not)
 *  and the converted feature collection.
 */
export const convertGPXToGeoJSON = async (file: File, type: GPXOption) => {
    const [parsed] = parseGPX(await fileToString(file));
    const result = convertParsedGPXToGeoJSON(parsed, type);
    return result;
};

/**
 * Converts parsed GPX to a GeoJSON Feature collection.
 * @returns An object containing:
 *  the options available inside the gpx content (i.e. routes and tracks),
 *  the selected option (based on whether the provided option exists or not)
 *  and the converted feature collection.
 */
export const convertParsedGPXToGeoJSON = (data: ParsedGPX, type: GPXOption) => {
    const options = Object.entries(data).reduce(
        (acc, [key, value]: [GPXOption, TrackGPX[] | RouteGPX[]]) => (["tracks", "routes"].includes(key) && value.length > 0 ? [...acc, key] : acc),
        [] as GPXOption[]
    );

    const selectedOption = options.includes(type) ? type : options[0];

    const features =
        data?.[selectedOption].map((object: TrackGPX | RouteGPX) => {
            let coordinates = object.points.map((point) => [point.longitude, point.latitude]);
            // If we are drawing tracks, we need to ensure we have the correct order using the time property
            if (selectedOption === "tracks") {
                const pointsWithTime = object.points.reduce<Date[]>((acc, { time }) => (time != null && isValidDate(time) ? [...acc, time] : acc), []);
                // If the first point is after the last point, reverse the coordinates
                if (pointsWithTime?.length > 1 && compareAsc(pointsWithTime[0], pointsWithTime[pointsWithTime.length - 1]) === 1) {
                    coordinates = coordinates.reverse();
                }
            }

            const elevationPositive = object?.elevation?.positive ?? 0;
            const elevationNegative = object?.elevation?.negative ?? 0;

            return {
                type: "Feature",
                geometry: {
                    type: "LineString",
                    coordinates,
                },
                properties: {
                    name: object.name,
                    comment: object.comment,
                    description: object.description,
                    src: object.src,
                    number: object.number,
                    link: object.link,
                    type: object.type,
                    distance: object.distance?.total,
                    "total increase elevation": Math.round(elevationPositive * 100) / 100,
                    "total decrease elevation": Math.round(elevationNegative * 100) / 100,
                    "net elevation": Math.round((elevationPositive - elevationNegative) * 100) / 100,
                    slopes: object.slopes,
                },
            } as const;
        }) ?? [];
    const featureCollection: FeatureCollection<LineString> = {
        features,
        type: "FeatureCollection",
    };

    return {
        options,
        selectedOption,
        featureCollection,
    };
};
